'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const PROJECTIONS = [
  {
    id: 1, home: 'TOR', away: 'BOS', time: '19:00', homeProb: 62.3, awayProb: 37.7,
    homeXG: 3.12, awayXG: 2.34, exactScore: '3-2', exactProb: 14.2,
    ciLow: 54.1, ciHigh: 70.5, volatility: 0.23, confidence: 81.2,
    momentum: { home: 72.3, away: 58.7 }, offPressure: { home: 68.4, away: 52.1 },
    scoringPace: 5.46, goalieImpact: { home: 0.87, away: 0.91 },
    fatigue: { home: 0.95, away: 0.98 }, playoffMod: 1.15,
    monteCarlo: { homeWins: 62.3, awayWins: 37.7, draws: 0 },
    distribution: [
      { score: '0-0', prob: 2.1 }, { score: '1-0', prob: 8.4 }, { score: '2-0', prob: 11.2 },
      { score: '3-0', prob: 9.8 }, { score: '1-1', prob: 7.3 }, { score: '2-1', prob: 13.4 },
      { score: '3-1', prob: 12.1 }, { score: '3-2', prob: 14.2 }, { score: '2-2', prob: 6.8 },
      { score: '4-2', prob: 8.9 }, { score: '4-3', prob: 5.8 },
    ],
  },
  {
    id: 2, home: 'EDM', away: 'VGK', time: '21:00', homeProb: 56.8, awayProb: 43.2,
    homeXG: 2.87, awayXG: 2.54, exactScore: '3-2', exactProb: 12.8,
    ciLow: 48.6, ciHigh: 65.0, volatility: 0.31, confidence: 74.6,
    momentum: { home: 61.2, away: 62.8 }, offPressure: { home: 61.2, away: 58.9 },
    scoringPace: 5.41, goalieImpact: { home: 0.92, away: 0.88 },
    fatigue: { home: 0.97, away: 0.94 }, playoffMod: 1.0,
    monteCarlo: { homeWins: 56.8, awayWins: 43.2, draws: 0 },
    distribution: [
      { score: '0-0', prob: 2.8 }, { score: '1-0', prob: 9.1 }, { score: '2-0', prob: 10.4 },
      { score: '1-1', prob: 8.9 }, { score: '2-1', prob: 14.2 }, { score: '3-2', prob: 12.8 },
      { score: '2-2', prob: 9.3 }, { score: '3-3', prob: 5.4 }, { score: '4-3', prob: 7.8 },
    ],
  },
  {
    id: 3, home: 'COL', away: 'DAL', time: '22:00', homeProb: 67.9, awayProb: 32.1,
    homeXG: 3.78, awayXG: 2.54, exactScore: '4-2', exactProb: 11.7,
    ciLow: 61.7, ciHigh: 74.1, volatility: 0.18, confidence: 85.7,
    momentum: { home: 78.4, away: 45.6 }, offPressure: { home: 74.2, away: 48.3 },
    scoringPace: 6.32, goalieImpact: { home: 0.94, away: 0.86 },
    fatigue: { home: 0.92, away: 0.97 }, playoffMod: 1.0,
    monteCarlo: { homeWins: 67.9, awayWins: 32.1, draws: 0 },
    distribution: [
      { score: '1-0', prob: 7.2 }, { score: '2-0', prob: 10.8 }, { score: '3-0', prob: 9.4 },
      { score: '4-0', prob: 6.1 }, { score: '2-1', prob: 11.3 }, { score: '3-1', prob: 13.2 },
      { score: '4-1', prob: 10.4 }, { score: '4-2', prob: 11.7 }, { score: '3-2', prob: 9.8 },
    ],
  },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

export default function AdvancedProjectionsClient() {
  const [selected, setSelected] = useState(PROJECTIONS[0]);
  const [simulating, setSimulating] = useState(false);
  const [simCount, setSimCount] = useState(10000);

  const handleSimulate = () => {
    setSimulating(true);
    setTimeout(() => setSimulating(false), 2500);
  };

  const volatilityColor = (v: number) => v < 0.2 ? 'text-positive' : v < 0.3 ? 'text-warning' : 'text-negative';
  const volatilityLabel = (v: number) => v < 0.2 ? 'Très Faible' : v < 0.3 ? 'Modérée' : 'Élevée';

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Projections Avancées</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Monte Carlo · Distributions · Intervalles de confiance · Modélisation game flow</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={simCount}
            onChange={(e) => setSimCount(Number(e.target.value))}
            className="bg-card border border-border rounded px-3 py-1.5 text-xs text-foreground"
          >
            <option value={1000}>1,000 simulations</option>
            <option value={10000}>10,000 simulations</option>
            <option value={100000}>100,000 simulations</option>
          </select>
          <button onClick={handleSimulate} disabled={simulating} className="btn-primary flex items-center gap-2 text-sm px-4 py-2">
            <Icon name="BeakerIcon" size={16} className={simulating ? 'animate-spin' : ''} />
            {simulating ? 'Simulation...' : 'Lancer Monte Carlo'}
          </button>
        </div>
      </div>

      {/* Game Selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PROJECTIONS.map((proj) => (
          <button
            key={proj.id}
            onClick={() => setSelected(proj)}
            className={`card p-4 text-left transition-all ${selected.id === proj.id ? 'border-primary/50 bg-primary/5' : 'hover:border-border/80'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-foreground">{proj.home} vs {proj.away}</span>
              <span className="text-xs text-muted-foreground font-mono-data">{proj.time}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-2 bg-card-hover rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${proj.homeProb}%` }} />
              </div>
              <span className="text-xs font-mono-data text-primary">{proj.homeProb}%</span>
            </div>
            <div className="flex justify-between text-2xs">
              <span className="text-muted-foreground">Score exact: <span className="text-foreground font-mono-data">{proj.exactScore}</span></span>
              <span className={`font-mono-data ${volatilityColor(proj.volatility)}`}>Vol: {volatilityLabel(proj.volatility)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Main Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Win Probability + CI */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Probabilités & Confiance</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold text-foreground">{selected.home}</span>
                <span className="font-mono-data text-primary">{selected.homeProb}%</span>
              </div>
              <div className="h-3 bg-card-hover rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full" style={{ width: `${selected.homeProb}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold text-foreground">{selected.away}</span>
                <span className="font-mono-data text-muted-foreground">{selected.awayProb}%</span>
              </div>
              <div className="h-3 bg-card-hover rounded-full overflow-hidden">
                <div className="h-full bg-card-hover/80 rounded-full border border-border" style={{ width: `${selected.awayProb}%` }} />
              </div>
            </div>
            <div className="pt-2 border-t border-border space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Intervalle confiance</span>
                <span className="font-mono-data text-foreground">[{selected.ciLow}% — {selected.ciHigh}%]</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Score exact prédit</span>
                <span className="font-mono-data text-primary">{selected.exactScore} ({selected.exactProb}%)</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Volatilité</span>
                <span className={`font-mono-data ${volatilityColor(selected.volatility)}`}>{selected.volatility} — {volatilityLabel(selected.volatility)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Indice confiance</span>
                <span className="font-mono-data text-positive">{selected.confidence}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Monte Carlo */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Monte Carlo — {simCount.toLocaleString()} simulations</h3>
          {simulating ? (
            <div className="flex flex-col items-center justify-center h-40 gap-3">
              <Icon name="BeakerIcon" size={32} className="text-primary animate-spin" />
              <span className="text-sm text-muted-foreground">Simulation en cours...</span>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-center">
                  <div className="text-2xl font-bold font-mono-data text-primary">{selected.monteCarlo.homeWins}%</div>
                  <div className="text-xs text-muted-foreground mt-1">{selected.home} Victoires</div>
                </div>
                <div className="p-3 rounded-lg bg-card-hover border border-border text-center">
                  <div className="text-2xl font-bold font-mono-data text-foreground">{selected.monteCarlo.awayWins}%</div>
                  <div className="text-xs text-muted-foreground mt-1">{selected.away} Victoires</div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">xGoals {selected.home}</span>
                  <span className="font-mono-data text-primary">{selected.homeXG}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">xGoals {selected.away}</span>
                  <span className="font-mono-data text-foreground">{selected.awayXG}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Scoring Pace</span>
                  <span className="font-mono-data text-foreground">{selected.scoringPace} buts/match</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Playoff Modifier</span>
                  <span className="font-mono-data text-warning">{selected.playoffMod}x</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Game Flow Factors */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Facteurs Game Flow</h3>
          <div className="space-y-3">
            {[
              { label: 'Momentum', home: selected.momentum.home, away: selected.momentum.away },
              { label: 'Pression Offensive', home: selected.offPressure.home, away: selected.offPressure.away },
              { label: 'Impact Gardien', home: selected.goalieImpact.home * 100, away: selected.goalieImpact.away * 100 },
              { label: 'Fatigue', home: selected.fatigue.home * 100, away: selected.fatigue.away * 100 },
            ].map((factor) => (
              <div key={factor.label}>
                <div className="flex justify-between text-2xs text-muted-foreground mb-1">
                  <span>{selected.home}</span>
                  <span className="font-medium text-foreground">{factor.label}</span>
                  <span>{selected.away}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="flex-1 h-2 bg-card-hover rounded-full overflow-hidden flex justify-end">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${factor.home}%` }} />
                  </div>
                  <div className="flex-1 h-2 bg-card-hover rounded-full overflow-hidden">
                    <div className="h-full bg-muted-foreground/40 rounded-full" style={{ width: `${factor.away}%` }} />
                  </div>
                </div>
                <div className="flex justify-between text-2xs font-mono-data mt-0.5">
                  <span className="text-primary">{factor.home.toFixed(1)}</span>
                  <span className="text-muted-foreground">{factor.away.toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Score Distribution */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Distribution des Probabilités — Scores Exacts</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={selected.distribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
            <XAxis dataKey="score" tick={{ fill: '#6B7A99', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6B7A99', fontSize: 10 }} unit="%" />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`, 'Probabilité']} />
            <Bar dataKey="prob" radius={[3, 3, 0, 0]} name="Probabilité">
              {selected.distribution.map((entry, index) => (
                <Cell key={index} fill={entry.score === selected.exactScore ? '#00D4FF' : '#1E2D4A'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
